import { useState } from "react";
import { collection, doc, addDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Add/remove schedule entries for a vendor or a specific worker (Phase
// 13). A Facility Admin's entries go live immediately (status:
// "approved"); a Vendor Admin can also propose one for their own crew,
// but it's created as status: "pending" and only counts once a Facility
// Admin approves it — firestore.rules enforces both the "vendor can only
// touch their own vendorId" and "vendor can't self-approve" halves of
// that, this is just the UI for it. Old entries predating this feature
// have no `status` field at all; treated as approved (`!== "pending"`)
// everywhere below, not migrated.
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dateLabel(dateStr) {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function ScheduleEditor({ facilityId, scopeType, scopeId, vendorId, schedules, viewerRole }) {
  const [adding, setAdding] = useState(false);
  const [mode, setMode] = useState("recurring"); // "recurring" | "one_time"
  const [label, setLabel] = useState("");
  const [days, setDays] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("10:00");
  const [err, setErr] = useState("");

  const toggleDay = (d) => setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());

  const isVendorAdmin = viewerRole === "vendor_admin";
  const effectiveVendorId = vendorId || (scopeType === "vendor" ? scopeId : null);

  const save = async () => {
    if (mode === "recurring" && days.length === 0) { setErr("Pick at least one day."); return; }
    if (mode === "one_time" && !date) { setErr("Pick a date."); return; }
    setErr("");
    try {
      await addDoc(collection(db, "schedules"), {
        facilityId, scopeType, scopeId, startTime, endTime,
        vendorId: effectiveVendorId,
        status: isVendorAdmin ? "pending" : "approved",
        daysOfWeek: mode === "recurring" ? days : null,
        date: mode === "one_time" ? date : null,
        label: label.trim() || null, createdAt: serverTimestamp(),
      });
      setLabel(""); setDays([]); setDate(todayStr()); setAdding(false);
    } catch (e) {
      setErr("Couldn't save — try again.");
    }
  };

  const remove = (id) => deleteDoc(doc(db, "schedules", id));
  const approve = (id) => updateDoc(doc(db, "schedules", id), { status: "approved" });

  return (
    <div>
      {schedules.length === 0 && !adding && <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>No schedule set.</div>}
      {schedules.map(s => (
        <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${C.border}`, gap: 8 }}>
          <div style={{ fontSize: 13 }}>
            {s.label && <b>{s.label} — </b>}
            {s.date ? dateLabel(s.date) : s.daysOfWeek.map(d => DAY_LABELS[d]).join(", ")}, {s.startTime}–{s.endTime}
            {s.date && <span style={{ ...S.pill(C.tintAccent, C.accent), marginLeft: 8 }}>One-time</span>}
            {s.status === "pending" && <span style={{ ...S.pill(C.tintWarning, "#92400E"), marginLeft: 8 }}>Pending approval</span>}
          </div>
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {s.status === "pending" && !isVendorAdmin && (
              <button style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px", color: C.green, borderColor: C.green }} onClick={() => approve(s.id)}>Approve</button>
            )}
            {(!isVendorAdmin || s.status === "pending") && (
              <button className="icon-tap-target" style={S.iconBtn} onClick={() => remove(s.id)}>×</button>
            )}
          </div>
        </div>
      ))}
      {!adding ? (
        <button style={{ ...S.ghostBtn, width: "auto", padding: "8px 14px", marginTop: 8, color: C.accent, borderColor: C.accent }} onClick={() => setAdding(true)}>{isVendorAdmin ? "+ Propose Schedule" : "+ Add Schedule"}</button>
      ) : (
        <div style={{ marginTop: 10, padding: 14, background: C.bg, borderRadius: 0 }}>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            <button onClick={() => setMode("recurring")} style={{ ...S.pill(mode === "recurring" ? C.ink : C.white, mode === "recurring" ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer" }}>Recurring</button>
            <button onClick={() => setMode("one_time")} style={{ ...S.pill(mode === "one_time" ? C.ink : C.white, mode === "one_time" ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer" }}>One-time</button>
          </div>
          {mode === "recurring" ? (
            <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
              {DAY_LABELS.map((d, i) => (
                <button key={i} onClick={() => toggleDay(i)} style={{ ...S.pill(days.includes(i) ? C.ink : C.white, days.includes(i) ? C.white : C.muted), border: `1px solid ${C.border}`, cursor: "pointer" }}>{d}</button>
              ))}
            </div>
          ) : (
            <input type="date" style={{ ...S.input, marginBottom: 10 }} value={date} onChange={e => setDate(e.target.value)} />
          )}
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input type="time" style={{ ...S.input, marginBottom: 0 }} value={startTime} onChange={e => setStartTime(e.target.value)} />
            <input type="time" style={{ ...S.input, marginBottom: 0 }} value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
          <input style={S.input} placeholder={mode === "one_time" ? "Label (optional, e.g. Deep clean)" : "Label (optional, e.g. Weekly service)"} value={label} onChange={e => setLabel(e.target.value)} />
          {isVendorAdmin && <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Sent to the facility admin for approval before it goes live.</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btn(C.green)} onClick={save}>{isVendorAdmin ? "Propose Schedule" : "Save"}</button>
            <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px" }} onClick={() => { setAdding(false); setErr(""); }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
